import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOCUS_OPTIONS = ["AI Exception Architecture", "Digital Adoption & In-App Guidance", "Behavioral Alignment"];

const EMPTY = { name: "", email: "", company: "", challenge: "" };

export default function Contact() {
  const [tab, setTab] = useState("assessment");
  const [fields, setFields] = useState(EMPTY);
  const [focus, setFocus] = useState([]);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  useEffect(() => {
    const handler = (e) => setTab(e.detail || "assessment");
    window.addEventListener("tk:tab", handler);
    return () => window.removeEventListener("tk:tab", handler);
  }, []);

  const set = (k) => (e) => setFields({ ...fields, [k]: e.target.value });

  const toggleFocus = (f) =>
    setFocus((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  const submit = async (e) => {
    e.preventDefault();
    const { name, email, challenge } = fields;
    if (!name.trim() || !email.trim() || !challenge.trim()) {
      setStatus({ state: "error", msg: "Please fill in your name, email, and the challenge you're facing." });
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setStatus({ state: "error", msg: "Please provide a valid email address." });
      return;
    }
    setStatus({ state: "submitting", msg: "" });
    try {
      await axios.post(`${API}/inquiries`, {
        inquiry_type: tab,
        name: name.trim(),
        email: email.trim(),
        company: fields.company.trim(),
        challenge: challenge.trim(),
        focus_areas: focus,
      });
      setStatus({ state: "success", msg: "" });
      setFields(EMPTY);
      setFocus([]);
      toast.success("Request received — we'll be in touch.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      setStatus({ state: "error", msg });
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#CBD5E1] bg-[#F8FAFC] px-4 py-3.5 text-sm text-[#0F141A] placeholder:text-[#8B95A5] transition-colors duration-200 focus:border-[#EB5E28]";

  return (
    <section id="contact" data-testid="contact-assessment-section" className="relative border-t border-[#E2E8F0] py-24 lg:py-32">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at 50% 0%, rgba(235,94,40,0.05) 0%, rgba(248,250,252,0) 60%)",
        }}
      />
      <div className="relative mx-auto max-w-4xl px-6 lg:px-12">
        <Reveal>
          <div className="mb-12 text-left">
            <p className="mb-5 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.35em] text-[#EB5E28]">
              <span className="h-px w-8 bg-[#EB5E28]/60" />
              [ 08 — Final Call to Action ]
            </p>
            <h2 className="font-display text-4xl font-semibold leading-[1.08] tracking-[-0.02em] text-[#0F141A] sm:text-5xl">
              Stop letting unplanned{" "}
              <span className="italic text-[#EB5E28]">exceptions</span> bottleneck your growth.
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#3B434E] sm:text-lg">
              Your technology should empower your workforce, not overwhelm them. Let's build the
              escalation architecture your AI needs and the clarity your team deserves.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative overflow-hidden rounded-3xl border border-[#E2E8F0] bg-white p-8 shadow-md lg:p-12">
            <div className="mb-10 inline-flex rounded-full border border-[#E2E8F0] bg-[#F1F5F9] p-1.5">
              {[
                { id: "assessment", label: "HITL Workflow Assessment", testid: "contact-tab-assessment" },
                { id: "consultation", label: "General Consultation", testid: "contact-tab-consultation" },
              ].map((t) => (
                <button
                  key={t.id}
                  data-testid={t.testid}
                  onClick={() => setTab(t.id)}
                  className={`rounded-full px-5 py-2.5 text-xs font-medium transition-colors duration-200 sm:text-sm ${
                    tab === t.id ? "bg-[#EB5E28] text-white shadow-sm" : "text-[#4A5361] hover:text-[#0F141A]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {status.state === "success" ? (
              <div
                data-testid="inquiry-status-message"
                className="flex flex-col items-start gap-4 rounded-2xl border border-[#EB5E28]/30 bg-[#FBF4F0] p-8"
              >
                <CheckCircle2 className="h-8 w-8 text-[#EB5E28]" />
                <div>
                  <p className="font-display text-2xl text-[#0F141A]">Request received.</p>
                  <p className="mt-2 text-sm leading-relaxed text-[#4A5361]">
                    Your{" "}
                    {tab === "assessment"
                      ? "HITL workflow assessment request"
                      : "consultation request"}{" "}
                    is in — we'll reach out at the email you provided.
                  </p>
                </div>
                <button
                  data-testid="inquiry-reset-btn"
                  onClick={() => setStatus({ state: "idle", msg: "" })}
                  className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#EB5E28]"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="inq-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#626D7D]">
                      Full Name *
                    </label>
                    <input
                      id="inq-name"
                      data-testid="inquiry-input-name"
                      className={inputCls}
                      placeholder="Jordan Smith"
                      value={fields.name}
                      onChange={set("name")}
                    />
                  </div>
                  <div>
                    <label htmlFor="inq-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#626D7D]">
                      Work Email *
                    </label>
                    <input
                      id="inq-email"
                      type="email"
                      data-testid="inquiry-input-email"
                      className={inputCls}
                      placeholder="jordan@company.com"
                      value={fields.email}
                      onChange={set("email")}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="inq-company" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#626D7D]">
                      Company
                    </label>
                    <input
                      id="inq-company"
                      data-testid="inquiry-input-company"
                      className={inputCls}
                      placeholder="Company name"
                      value={fields.company}
                      onChange={set("company")}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="inq-challenge" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#626D7D]">
                      Where does your workflow break down? *
                    </label>
                    <textarea
                      id="inq-challenge"
                      data-testid="inquiry-input-challenge"
                      rows={4}
                      className={`${inputCls} resize-none`}
                      placeholder="Describe the automated process, the exceptions it drops on your team, or the target you're missing…"
                      value={fields.challenge}
                      onChange={set("challenge")}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#626D7D]">
                      Preferred Focus
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {FOCUS_OPTIONS.map((f) => (
                        <label
                          key={f}
                          className={`flex cursor-pointer items-center gap-2.5 rounded-full border px-4 py-2.5 text-xs transition-colors duration-200 ${
                            focus.includes(f)
                              ? "border-[#EB5E28]/60 bg-[#FBF4F0] text-[#0F141A]"
                              : "border-[#E2E8F0] bg-white text-[#4A5361] hover:border-[#CBD5E1]"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 accent-[#EB5E28]"
                            checked={focus.includes(f)}
                            onChange={() => toggleFocus(f)}
                            data-testid={`inquiry-checkbox-${f.split(" ")[0].toLowerCase()}`}
                          />
                          {f}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {status.state === "error" && (
                  <p
                    data-testid="inquiry-error-message"
                    className="mt-5 rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-600"
                  >
                    {status.msg}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="inquiry-submit-btn"
                  disabled={status.state === "submitting"}
                  className="group mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#EB5E28] px-8 py-4 text-sm font-semibold text-white shadow-md shadow-[#EB5E28]/20 transition-colors duration-200 hover:bg-[#D94E1B] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {status.state === "submitting" ? (
                    <>
                      Sending
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                  ) : (
                    <>
                      {tab === "assessment" ? "Book Your 30-Minute Workflow Audit" : "Book a Consultation"}
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
